/**
 * Energy Certificate Simulator - Zod Validation Layer
 *
 * Runtime validation for SimulatorInput before EP calculation.
 * Provides type-safe parsing with detailed error messages.
 */

import { z } from 'zod';

// =============================================================================
// ENUM SCHEMAS
// =============================================================================

const BuildingPeriodSchema = z.enum([
  'before_1945', '1945_1966', '1967_1985', '1986_1998',
  '1999_2008', '2009_2013', '2014_2020', '2021_plus',
]);

const BuildingTypeSchema = z.enum([
  'detached', 'semi_detached', 'terraced', 'apartment',
]);

const ApartmentFloorPositionSchema = z.enum([
  'ground', 'middle', 'top',
]);

const ApartmentOrientationSchema = z.enum([
  'north', 'south', 'east', 'west',
  'northeast', 'northwest', 'southeast', 'southwest',
]);

const RenovationStatusSchema = z.enum(['none', 'partial', 'full']);

const InsulationLevelSchema = z.enum(['none', 'partial', 'full', 'modern']);

const WindowTypeSchema = z.enum(['single', 'double_old', 'double_new', 'triple']);

const HeatingSystemSchema = z.enum([
  'gas_condensing', 'gas_traditional', 'coal', 'biomass',
  'heat_pump_air', 'heat_pump_ground', 'electric_radiators',
  'electric_floor', 'district_heating', 'stove',
]);

const SystemAgeSchema = z.enum(['new', 'medium', 'old']);

const HotWaterSourceSchema = z.enum(['combined', 'electric', 'gas', 'solar']);

const RenewableSourceSchema = z.enum([
  'solar_thermal', 'recuperation', 'ground_exchanger',
]);

// =============================================================================
// SIMULATOR INPUT SCHEMA
// =============================================================================

export const SimulatorInputSchema = z.object({
  // Building parameters
  usableArea: z.number().min(20, 'Powierzchnia min. 20 m²').max(500, 'Powierzchnia max. 500 m²'),
  buildingPeriod: BuildingPeriodSchema,
  buildingType: BuildingTypeSchema,
  floors: z.number().int().min(1).max(10),
  occupants: z.number().int().min(1).max(10),
  apartmentFloorPosition: ApartmentFloorPositionSchema.optional(),
  apartmentOrientation: ApartmentOrientationSchema.optional(),
  climateZone: z.enum(['I', 'II', 'III', 'IV', 'V']).optional(),

  // Renovation parameters
  renovationStatus: RenovationStatusSchema,
  renovationYear: z.number().int().min(1990).max(2024).optional(),

  // Insulation parameters
  wallInsulation: InsulationLevelSchema,
  roofInsulation: InsulationLevelSchema,
  windowType: WindowTypeSchema,

  // Heating parameters
  heatingSystem: HeatingSystemSchema,
  systemAge: SystemAgeSchema,
  hotWaterSource: HotWaterSourceSchema,
  hasSecondaryHeating: z.boolean(),
  secondaryHeatingSystem: HeatingSystemSchema.optional(),
  secondaryHeatingShare: z.number().min(10).max(50).optional(),

  // Renewable energy parameters
  hasPV: z.boolean(),
  pvPower: z.number().max(50).optional(),
  renewables: z.array(RenewableSourceSchema),

  // Hot water overrides (advanced)
  dailyHotWaterPerPerson: z.number().min(20).max(100).optional(),
  hotWaterTemp: z.number().min(40).max(70).optional(),
  coldWaterTemp: z.number().min(4).max(20).optional(),
}).refine(
  (data) => !data.hasPV || (data.pvPower !== undefined && data.pvPower > 0),
  { message: 'pvPower required when hasPV is true', path: ['pvPower'] }
).refine(
  (data) => data.renovationStatus === 'none' || data.renovationYear !== undefined,
  { message: 'renovationYear required when renovated', path: ['renovationYear'] }
).refine(
  (data) => !data.hasSecondaryHeating || data.secondaryHeatingSystem !== undefined,
  { message: 'secondaryHeatingSystem required when hasSecondaryHeating is true', path: ['secondaryHeatingSystem'] }
).refine(
  (data) => !data.hasSecondaryHeating || (data.secondaryHeatingShare !== undefined && data.secondaryHeatingShare >= 10 && data.secondaryHeatingShare <= 50),
  { message: 'secondaryHeatingShare (10-50) required when hasSecondaryHeating is true', path: ['secondaryHeatingShare'] }
).refine(
  (data) => data.buildingType !== 'apartment' || data.apartmentFloorPosition !== undefined,
  { message: 'apartmentFloorPosition required for apartment buildings', path: ['apartmentFloorPosition'] }
).refine(
  (data) => data.buildingType !== 'apartment' || data.apartmentOrientation !== undefined,
  { message: 'apartmentOrientation required for apartment buildings', path: ['apartmentOrientation'] }
);

export type ValidatedSimulatorInput = z.infer<typeof SimulatorInputSchema>;

// =============================================================================
// VALIDATION FUNCTION
// =============================================================================

export class SimulatorValidationError extends Error {
  constructor(
    message: string,
    public readonly issues: z.ZodIssue[],
  ) {
    super(message);
    this.name = 'SimulatorValidationError';
  }
}

/**
 * Validate SimulatorInput at runtime using Zod.
 * Returns the validated (and typed) input.
 * Throws SimulatorValidationError if invalid.
 */
export function validateSimulatorInput(input: unknown): ValidatedSimulatorInput {
  const result = SimulatorInputSchema.safeParse(input);
  if (!result.success) {
    const messages = result.error.issues.map(
      (i) => `${i.path.join('.')}: ${i.message}`
    );
    throw new SimulatorValidationError(
      `Invalid simulator input: ${messages.join('; ')}`,
      result.error.issues,
    );
  }
  return result.data;
}
